import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle,
  LinearProgress, Stack, TextField, Typography,
} from '@mui/material';
import axios from 'axios';
import { collection, doc, getDoc, getDocs, getFirestore, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import firebaseApp from '../../utils/firebaseConfig';
import { pickBestMember } from '../../utils/members';

const db = getFirestore(firebaseApp);
const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';
const SESSION_KEY = 'reservation_user';
const digits = (v) => String(v || '').replace(/\D/g, '');
const RED = { bgcolor: '#d40100', '&:hover': { bgcolor: '#a30000' } };

// Página pública /waitlist: el cliente entra con TELÉFONO + PIN (misma sesión
// que /layaways y /make-reservation), ve en qué listas está y su posición, se
// anota en productos nuevos o se sale. Sin cuenta: se le crea con nombre +
// email + PIN (pedido Lola 2026-10-19, estilo Waitwhile).
export default function MyWaitlistPage() {
  const [loginInput, setLoginInput] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [user, setUser] = useState(null);
  const [pinStep, setPinStep] = useState(null); // verify | create | signup
  const [pinValue, setPinValue] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinVerified, setPinVerified] = useState(false);
  const [signup, setSignup] = useState({ name: '', email: '' });

  const [data, setData] = useState({ products: [], entries: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(null); // product
  const [joinNote, setJoinNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    try {
      const saved = localStorage.getItem(SESSION_KEY);
      if (saved) { const p = JSON.parse(saved); if (p && (p.phone || p.email)) { setUser(p); setPinVerified(true); } }
    } catch (e) { /* ignore */ }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.post(`${FUNCTIONS_BASE}/getWaitlist`, { memberId: (user && (user.id || user.docId)) || null, phone: user ? digits(user.phone) || null : null, email: (user && user.email) || null });
      setData({ products: r.data.products || [], entries: r.data.entries || [] });
    } catch (e) { setError((e.response && e.response.data && e.response.data.error) || 'No se pudo cargar la lista'); } finally { setLoading(false); }
  }, [user]);
  useEffect(() => { if (user && pinVerified) fetchAll(); }, [user, pinVerified, fetchAll]);

  const handleLogin = async () => {
    const raw = loginInput.trim();
    if (!raw) { setLoginError('Escribe tu número de teléfono'); return; }
    setLoginLoading(true); setLoginError('');
    try {
      const isPhone = digits(raw).length >= 7 && !raw.includes('@');
      const phone = isPhone ? digits(raw) : '';
      const email = isPhone ? '' : raw.toLowerCase();
      let snap = await getDocs(query(collection(db, 'members'), where(isPhone ? 'phone' : 'email', '==', isPhone ? phone : email)));
      if (snap.empty && isPhone && phone.length === 10) snap = await getDocs(query(collection(db, 'members'), where('phone', '==', `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`)));
      if (snap.empty) {
        if (!isPhone) { setLoginError('No encontramos una cuenta con ese email. Entra con tu teléfono.'); setLoginLoading(false); return; }
        setUser({ phone, email: '' }); setPinStep('signup'); setLoginLoading(false); return;
      }
      const m = pickBestMember(snap.docs, { phone, email });
      const u = { ...m, phone: m.phone ? digits(m.phone) : phone, email: (m.email || email || '').toLowerCase() };
      setUser(u);
      try {
        const pinSnap = await getDoc(doc(db, 'user-pins', u.phone || u.email));
        setPinStep(pinSnap.exists() || u.pin ? 'verify' : 'create');
      } catch (e) { setPinStep('create'); }
    } catch (e) { setLoginError('Error al verificar tu cuenta'); }
    setLoginLoading(false);
  };
  const finishLogin = (u) => { localStorage.setItem(SESSION_KEY, JSON.stringify({ ...u })); setUser(u); setPinVerified(true); setPinStep(null); };
  const handlePinVerify = async () => {
    if (pinValue.length !== 4) { setPinError('El PIN debe ser de 4 dígitos'); return; }
    try {
      const pinSnap = await getDoc(doc(db, 'user-pins', user.phone || user.email));
      const ok = (pinSnap.exists() && pinSnap.data().pin === pinValue) || (user.pin && String(user.pin) === pinValue);
      if (ok) finishLogin(user); else { setPinError('PIN incorrecto. Intenta de nuevo.'); setPinValue(''); }
    } catch (e) { setPinError('Error al verificar el PIN'); }
  };
  const handlePinCreate = async () => {
    if (pinValue.length !== 4) { setPinError('El PIN debe ser de 4 dígitos'); return; }
    if (pinValue !== pinConfirm) { setPinError('Los PINs no coinciden'); setPinConfirm(''); return; }
    try {
      await setDoc(doc(db, 'user-pins', user.phone || user.email), { pin: pinValue, createdAt: serverTimestamp() });
      if (user.id) await setDoc(doc(db, 'members', user.id), { pin: pinValue }, { merge: true });
      finishLogin(user);
    } catch (e) { setPinError('Error al guardar el PIN'); }
  };
  // Cliente nuevo: nombre + email + PIN. La cuenta la crea joinWaitlist al
  // anotarse por primera vez; mientras, la sesión guarda los datos.
  const handleSignup = () => {
    if (!signup.name.trim()) { setPinError('Escribe tu nombre'); return; }
    if (pinValue.length !== 4) { setPinError('El PIN debe ser de 4 dígitos'); return; }
    if (pinValue !== pinConfirm) { setPinError('Los PINs no coinciden'); setPinConfirm(''); return; }
    finishLogin({ phone: user.phone, email: signup.email.trim().toLowerCase(), fullName: signup.name.trim(), pin: pinValue, isNew: true });
  };
  const handleLogout = () => { localStorage.removeItem(SESSION_KEY); setUser(null); setPinVerified(false); setPinStep(null); setPinValue(''); setLoginInput(''); setData({ products: [], entries: [] }); };

  const join = async () => {
    if (!joining) return;
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${FUNCTIONS_BASE}/joinWaitlist`, {
        productId: joining.id, memberId: user.id || user.docId || null, phone: digits(user.phone) || null, email: user.email || null,
        name: user.fullName || '', note: joinNote, source: 'web', pin: user.isNew ? user.pin : undefined,
      });
      if (r.data.memberId && !user.id) finishLogin({ ...user, id: r.data.memberId, isNew: false });
      setFlash(r.data.alreadyIn ? `Ya estabas en la lista de ${joining.name} (posición ${r.data.entry.position}).` : `¡Listo! Estás en la posición ${r.data.entry.position} para ${joining.name}. Te avisamos por email${r.data.entry.email ? ` (${r.data.entry.email})` : ''} y por la app cuando llegue.`);
      setJoining(null); setJoinNote('');
      await fetchAll();
    } catch (e) { setError((e.response && e.response.data && e.response.data.error) || e.message); } finally { setBusy(false); }
  };
  const leave = async (entry) => {
    if (!window.confirm(`¿Salir de la lista de ${entry.productName}? Perderías tu posición.`)) return;
    setBusy(true);
    try {
      await axios.post(`${FUNCTIONS_BASE}/leaveWaitlist`, { entryId: entry.id, memberId: user.id || user.docId || null, phone: digits(user.phone) || null, email: user.email || null });
      await fetchAll();
    } catch (e) { setError((e.response && e.response.data && e.response.data.error) || e.message); } finally { setBusy(false); }
  };

  const header = (
    <Box sx={{ bgcolor: '#1a1a1a', color: '#fff', py: 3, mb: 3 }}>
      <Container maxWidth="sm">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <div>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>Lista de espera</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>B&B Target Center · Te avisamos cuando llegue</Typography>
          </div>
          {user && pinVerified && <Button size="small" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }} onClick={handleLogout}>Salir</Button>}
        </Stack>
      </Container>
    </Box>
  );

  if (!user || !pinVerified) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F4F6F8' }}>
        <Helmet><title>Lista de espera | B&B Target Center</title></Helmet>
        {header}
        <Container maxWidth="sm">
          <Card sx={{ p: 3 }}>
            {!user && (
              <Stack spacing={2}>
                <Typography variant="h6">Entra con tu teléfono</Typography>
                <Typography variant="body2" color="text.secondary">Si no tienes cuenta, te la creamos en un paso.</Typography>
                {loginError && <Alert severity="error">{loginError}</Alert>}
                <TextField label="Teléfono" value={loginInput} onChange={(e) => setLoginInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleLogin()} placeholder="787-555-1234" fullWidth />
                <Button variant="contained" size="large" onClick={handleLogin} disabled={loginLoading} sx={RED}>{loginLoading ? <CircularProgress size={22} color="inherit" /> : 'Continuar'}</Button>
              </Stack>
            )}
            {user && pinStep === 'verify' && (
              <Stack spacing={2}>
                <Typography variant="h6">Hola{user.fullName ? `, ${user.fullName.split(' ')[0]}` : ''} 👋</Typography>
                <Typography variant="body2" color="text.secondary">Escribe tu PIN de 4 dígitos.</Typography>
                {pinError && <Alert severity="error">{pinError}</Alert>}
                <TextField label="PIN" type="password" inputProps={{ inputMode: 'numeric', maxLength: 4, style: { textAlign: 'center', fontSize: 24, letterSpacing: 12 } }} value={pinValue} onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))} onKeyDown={(e) => e.key === 'Enter' && handlePinVerify()} />
                <Button variant="contained" size="large" onClick={handlePinVerify} sx={RED}>Entrar</Button>
                <Button size="small" onClick={() => { setUser(null); setPinStep(null); setPinValue(''); }}>Usar otro teléfono</Button>
              </Stack>
            )}
            {user && pinStep === 'create' && (
              <Stack spacing={2}>
                <Typography variant="h6">Crea tu PIN</Typography>
                <Typography variant="body2" color="text.secondary">Clave de 4 dígitos para entrar la próxima vez (también sirve en la app).</Typography>
                {pinError && <Alert severity="error">{pinError}</Alert>}
                <TextField label="PIN (4 dígitos)" type="password" inputProps={{ inputMode: 'numeric', maxLength: 4 }} value={pinValue} onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))} />
                <TextField label="Confirmar PIN" type="password" inputProps={{ inputMode: 'numeric', maxLength: 4 }} value={pinConfirm} onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))} />
                <Button variant="contained" size="large" onClick={handlePinCreate} sx={RED}>Guardar y entrar</Button>
              </Stack>
            )}
            {user && pinStep === 'signup' && (
              <Stack spacing={2}>
                <Typography variant="h6">Cuenta nueva</Typography>
                <Typography variant="body2" color="text.secondary">No tenemos cuenta con el {user.phone}. Dinos tu nombre y un email para avisarte, y crea tu PIN.</Typography>
                {pinError && <Alert severity="error">{pinError}</Alert>}
                <TextField label="Nombre completo" value={signup.name} onChange={(e) => setSignup({ ...signup, name: e.target.value })} />
                <TextField label="Email (para el aviso)" value={signup.email} onChange={(e) => setSignup({ ...signup, email: e.target.value })} />
                <TextField label="PIN (4 dígitos)" type="password" inputProps={{ inputMode: 'numeric', maxLength: 4 }} value={pinValue} onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))} />
                <TextField label="Confirmar PIN" type="password" inputProps={{ inputMode: 'numeric', maxLength: 4 }} value={pinConfirm} onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))} />
                <Button variant="contained" size="large" onClick={handleSignup} sx={RED}>Continuar</Button>
                <Button size="small" onClick={() => { setUser(null); setPinStep(null); setPinValue(''); }}>Usar otro teléfono</Button>
              </Stack>
            )}
          </Card>
        </Container>
      </Box>
    );
  }

  const mine = data.entries;
  const inList = new Set(mine.filter((e) => ['waiting', 'notified'].includes(e.status)).map((e) => e.productId));
  const norm = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const others = data.products.filter((p) => !inList.has(p.id)).filter((p) => !search.trim() || norm(`${p.name} ${p.description || ''}`).includes(norm(search)));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F4F6F8', pb: 6 }}>
      <Helmet><title>Lista de espera | B&B Target Center</title></Helmet>
      {header}
      <Container maxWidth="sm">
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {flash && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setFlash(null)}>{flash}</Alert>}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Typography variant="h6" sx={{ mb: 1 }}>Mis listas</Typography>
        {!loading && mine.length === 0 && <Card sx={{ p: 3, mb: 3 }}><Typography variant="body2" color="text.secondary">No estás en ninguna lista. Anótate abajo en el producto que esperas.</Typography></Card>}
        <Stack spacing={2} sx={{ mb: 3 }}>
          {mine.map((e) => (
            <Card key={e.id} sx={{ p: 2, borderLeft: 5, borderColor: e.status === 'notified' ? '#2E7D32' : e.status === 'waiting' ? '#1565C0' : '#bbb' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <div>
                  <Typography variant="subtitle1" fontWeight={700}>{e.productName}</Typography>
                  {e.status === 'notified' && <Typography variant="body2" color="success.main" fontWeight={600}>¡Ya está disponible! Pasa por la tienda o llama al 787-755-9060.</Typography>}
                  {e.status === 'waiting' && <Typography variant="body2">Vas en la posición <b style={{ fontSize: 20 }}>#{e.position}</b>. Te avisamos cuando llegue.</Typography>}
                  {e.status === 'fulfilled' && <Typography variant="body2" color="text.secondary">Entregado.</Typography>}
                  <Typography variant="caption" color="text.secondary">Te anotaste el {e.joinedAt ? new Date(e.joinedAt).toLocaleDateString('es-PR', { day: 'numeric', month: 'long' }) : ''}{e.note ? ` · ${e.note}` : ''}</Typography>
                </div>
                <Stack alignItems="flex-end" spacing={1}>
                  <Chip size="small" color={e.status === 'notified' ? 'success' : e.status === 'waiting' ? 'info' : 'default'} label={e.status === 'notified' ? 'Disponible' : e.status === 'waiting' ? 'En espera' : 'Entregado'} />
                  {['waiting', 'notified'].includes(e.status) && <Button size="small" color="error" onClick={() => leave(e)} disabled={busy}>Salir</Button>}
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>

        <Typography variant="h6" sx={{ mb: 1 }}>Anotarme</Typography>
        {data.products.length > 3 && <TextField fullWidth size="small" placeholder="Buscar producto…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 2, bgcolor: '#fff' }} />}
        {!loading && others.length === 0 && <Card sx={{ p: 3 }}><Typography variant="body2" color="text.secondary">{search ? `Nada coincide con "${search}".` : 'No hay más productos con lista de espera por ahora.'}</Typography></Card>}
        <Stack spacing={2}>
          {others.map((p) => (
            <Card key={p.id} sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                {p.imageUrl && <Box component="img" src={p.imageUrl} alt={p.name} sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0 }} />}
                <div style={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>{p.name}</Typography>
                  {p.description && <Typography variant="body2" color="text.secondary">{p.description}</Typography>}
                  <Typography variant="caption" color="text.secondary">{p.waitingCount} persona{p.waitingCount === 1 ? '' : 's'} en lista{p.available ? ' · disponible ahora' : ''}</Typography>
                </div>
                <Button variant="contained" sx={RED} onClick={() => { setJoining(p); setJoinNote(''); }} disabled={busy}>Anotarme</Button>
              </Stack>
            </Card>
          ))}
        </Stack>
      </Container>

      <Dialog open={Boolean(joining)} onClose={() => setJoining(null)} maxWidth="xs" fullWidth>
        {joining && (
          <>
            <DialogTitle>Anotarme: {joining.name}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                {joining.imageUrl && <Box component="img" src={joining.imageUrl} alt={joining.name} sx={{ width: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 1.5, bgcolor: '#f5f5f5' }} />}
                <Typography variant="body2">Entras al final de la lista ({joining.waitingCount} delante de ti). Cuando llegue, te avisamos por email{user.email ? ` a ${user.email}` : ''} y por la app.</Typography>
                <TextField fullWidth label="Nota (opcional)" value={joinNote} onChange={(e) => setJoinNote(e.target.value)} placeholder="Ej: quiero 2, color negro" />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setJoining(null)} disabled={busy}>Cancelar</Button>
              <Button variant="contained" sx={RED} onClick={join} disabled={busy}>{busy ? 'Anotando…' : 'Confirmar'}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
